import { SellerMemberMissionDetailResponse } from "../../../api/pub-mission-member";
import ProgressStepper from "./progress-stepper";
import * as S from "../styles";

interface MissionDetailPanelProps {
  detail: SellerMemberMissionDetailResponse | null;
  loading: boolean;
  progressInput: string;
  onProgressInputChange: (value: string) => void;
  onComplete: () => void;
  onCancelComplete: () => void;
  onSaveProgress: () => void;
}

const MissionDetailPanel = ({
  detail,
  loading,
  progressInput,
  onProgressInputChange,
  onComplete,
  onCancelComplete,
  onSaveProgress,
}: MissionDetailPanelProps) => {
  if (loading) {
    return (
      <S.DetailPanel>
        <S.PanelHeader>미션 상세</S.PanelHeader>
        <S.PanelBody>
          <S.EmptyMessage>불러오는 중...</S.EmptyMessage>
        </S.PanelBody>
      </S.DetailPanel>
    );
  }

  if (!detail) return null;

  const isCumulative = detail.type === "CUMULATIVE";
  const target = detail.targetCount ?? 0;
  const current = detail.currentCount;
  const progressPercent =
    isCumulative && target > 0
      ? Math.min(100, Math.round((current / target) * 100))
      : 0;

  return (
    <S.DetailPanel>
      <S.PanelHeader>미션 상세</S.PanelHeader>
      <S.PanelBody>
        <S.DetailTitle>{detail.name}</S.DetailTitle>

        <S.BadgeRow>
          <S.Badge $variant={detail.isComplete ? "dark" : "default"}>
            {detail.progressStatusLabel}
          </S.Badge>
          <S.Badge>{detail.typeLabel}</S.Badge>
        </S.BadgeRow>

        {isCumulative ? (
          <S.ProgressSection>
            <S.ProgressText>
              {current} / {target}
            </S.ProgressText>
            <S.ProgressBar>
              <div className="fill" style={{ width: `${progressPercent}%` }} />
            </S.ProgressBar>
            <ProgressStepper
              value={progressInput}
              max={target}
              onChange={onProgressInputChange}
              onSave={onSaveProgress}
            />
            <S.SubText>목표 수치 달성 시 완료 상태로 전환됩니다.</S.SubText>
          </S.ProgressSection>
        ) : (
          <>
            {detail.isComplete ? (
              <S.ActionButton
                type="button"
                $variant="danger"
                onClick={onCancelComplete}
              >
                완료 취소
              </S.ActionButton>
            ) : (
              <S.ActionButton type="button" onClick={onComplete}>
                완료 처리
              </S.ActionButton>
            )}
          </>
        )}

        <S.SectionTitle>달성 보상</S.SectionTitle>
        <S.RewardTable>
          <colgroup>
            <col style={{ width: "22%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>보상명</th>
              <th>유형</th>
              <th>수량</th>
              <th>달성 단계</th>
              <th>지급 상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {detail.rewards.map((reward) => (
              <tr key={reward.id}>
                <td>{reward.name}</td>
                <td>{reward.typeLabel}</td>
                <td>{reward.count ?? "-"}</td>
                <td>{reward.stageLabel}</td>
                <td>
                  <S.StatusBadge $status={reward.payoutStatusLabel}>
                    {reward.payoutStatusLabel}
                  </S.StatusBadge>
                </td>
                <td>—</td>
              </tr>
            ))}
          </tbody>
        </S.RewardTable>
      </S.PanelBody>
    </S.DetailPanel>
  );
};

export default MissionDetailPanel;
